import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type { ConfigType } from '@/time';
import { dayjs } from '@/time';

const UPDATE_INTERVAL = 60 * 1000; // 1 min

type Props = {
  time: ConfigType;
  withoutSuffix?: boolean;
};

export const PastTime: FC<Props> = ({ time, withoutSuffix = false }) => {
  const { t } = useTranslation(LocalizationNameSpace.SHARED, {
    keyPrefix: 'pastTime'
  });
  const [, setCount] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setCount((c) => c + 1), UPDATE_INTERVAL);
    return () => clearTimeout(timer);
  }, []);

  const pastTime = dayjs(time);
  const now = dayjs();

  const diff = dayjs.duration(pastTime.diff(now));

  // within the last minute
  // There are posts whose time is deliberately far in the future so that they are displayed here as “Just now”
  if (diff.asMinutes() >= -1) {
    return t('justNow');
  }

  // within the last 24 hours
  if (diff.asDays() > -1) {
    return diff.humanize(!withoutSuffix);
  }

  // within the last 24 to 48 hours
  if (diff.asDays() >= -2) {
    return t('yesterday', { time: pastTime.format('HH:mm') });
  }

  // within the last 2 to 7 days
  if (diff.asDays() <= -2 && diff.asDays() > -7) {
    return diff.humanize(!withoutSuffix);
  }

  /*
  // within the last 7 days
  if (diff.asWeeks() > -1) {
    return pastTime.format('dd, HH:mm');
  }
  */

  // within the same year
  if (pastTime.isSame(now, 'year')) {
    return pastTime.format('DD. MMM');
  }

  return pastTime.format('DD. MMM YY');
};
